import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { notification } from "antd";
import firebaseApp from "firebase/app";
import firebase from "FirebaseConfig.js";
import { apiRequest } from "util/services";
import { logError } from "util/services";
import {
  preferredVerificationLanguage,
  sendAppVerificationEmail,
} from "util/sendVerificationEmail";
import Representation from "./Representation";
import { useTranslation } from "react-i18next";

const MFA_TIMER = 120;

const Security = ({ user }) => {
  const history = useHistory();
  const [email, setEmail] = useState(user?.email);
  const [emailVerified, setEmailVerified] = useState(user?.emailVerified);
  const [changeEmail, setChangeEmail] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [oldPassword, setOldPassword] = useState();
  const [newPassword, setNewPassword] = useState();
  const [confirmNewPassword, setConfirmNewPassword] = useState();
  const [mfaEnabled, setMFAEnabled] = useState();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState();
  const [changePwdLoading, setChangePwdLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // MFA modal state — phone number pre-filled from account
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [mfaPhone, setMfaPhone] = useState(user?.phoneNumber || "");
  const [mfaSmsSent, setMfaSmsSent] = useState(false);
  const [mfaVerificationId, setMfaVerificationId] = useState();
  const [mfaVerificationCode, setMfaVerificationCode] = useState();
  const [mfaTimeLeft, setMfaTimeLeft] = useState(0);
  const [mfaLoading, setMfaLoading] = useState(false);
  const recaptchaVerifierRef = useRef(null);

  const { t, i18n } = useTranslation();

  // Listen to Firebase Auth state changes to update emailVerified status
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((authUser) => {
      if (authUser) {
        setEmailVerified(authUser.emailVerified);
        // Update email if it changed in Firebase Auth - BUT ONLY if user is NOT in edit mode
        if (authUser.email && authUser.email !== email && !changeEmail) {
          setEmail(authUser.email);
        }
      }
    });
    return () => unsubscribe();
  }, [email, changeEmail]);

  useEffect(() => {
    setMFAEnabled(
      firebase.auth().currentUser.multiFactor.enrolledFactors.length !== 0
    );
    const payload = {
      user_id: user.uid,
    };
    apiRequest("subscriptions/all", "POST", payload, true).then((result) => {
      if (!result.error) {
        setSubscriptionStatus(result?.[0]);
      } else {
        notification.error({
          message: result.error,
        });
      }
    });
  }, [user.uid]);

  // Cooldown timer effect
  useEffect(() => {
    let interval = null;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  const changePassword = () => {
    if (confirmNewPassword !== newPassword) {
      notification.error({ message: t("notifications.102") });
    } else {
      setChangePwdLoading(true);
      firebase
        .auth()
        .signInWithEmailAndPassword(user.email, oldPassword)
        .then((userCredential) => {
          userCredential.user
            .updatePassword(newPassword)
            .then(() => {
              setShowPasswordModal(false);
              setChangePwdLoading(false);
              notification.success({ message: t("notifications.103") });
            })
            .catch((e) => {
              setShowPasswordModal(false);
              setChangePwdLoading(false);
              notification.error({
                message: `${t("notifications.104")}: ` + e.message,
              });
            });
        });
    }
  };

  const deleteAccount = () => {
    // Account deletion is handled via email request to info@pivony.com
    // The Representation layer opens the mailto link and closes the modal
    setShowDeleteModal(false);
  };

  const saveEmail = () => {
    // Check if email actually changed
    if (email.toLowerCase().trim() === user?.email?.toLowerCase().trim()) {
      notification.info({
        message: "No Change",
        description: "Email address is the same. No update needed.",
        duration: 4,
      });
      setChangeEmail(false);
      return;
    }

    firebase
      .auth()
      .currentUser.updateEmail(email)
      .then(async () => {
        firebase
          .database()
          .ref(`/users/${user.uid}/email`)
          .set(email);

        const payload = {
          user_id: user.uid,
          email: email,
        };
        const response = await apiRequest("users/update_email", "POST", payload);
        
        // Check if there's an error (e.g., email already in use)
        if (response && response.error) {
          notification.error({
            message: "Email Update Failed",
            description: response.error || "This email address is already in use. Please use a different email address.",
            duration: 6,
          });
          // Reset email to original value
          setEmail(user?.email);
          setChangeEmail(false);
          return;
        }
        
        // Check if email didn't change (backend response)
        if (response && response.success && !response.email_verification_required) {
          notification.info({
            message: "No Change",
            description: response.message || "Email address is the same. No update needed.",
            duration: 4,
          });
          setChangeEmail(false);
          return;
        }
        
        // Check if email verification is required
        if (response && response.email_verification_required) {
          // Update local state - email is updated but not verified yet
          setEmailVerified(false);
          
          // Refresh Firebase Auth user to get latest emailVerified status
          firebase.auth().currentUser.reload().then(() => {
            const updatedUser = firebase.auth().currentUser;
            setEmailVerified(updatedUser.emailVerified);
          });
          
          sendAppVerificationEmail({
            uid: firebase.auth().currentUser?.uid,
            language: preferredVerificationLanguage(i18n.language),
            continueUrl: `${window.location.origin}/home`,
            firstName: user?.firstName,
          }).then((result) => {
            if (result && !result.error && !result.cancel) {
              notification.warning({
                message: "Email Updated",
                description: "Please verify your new email address. A verification email has been sent to your inbox.",
                duration: 8,
              });
              return;
            }
            notification.warning({
              message: "Email Updated",
              description: "Please verify your new email address. Failed to send verification email automatically. Please use the resend button.",
              duration: 8,
            });
          });
        } else {
          // Email updated successfully without verification requirement
          // Only update email field, don't overwrite entire user object (preserve plan field)
          await firebase
            .database()
            .ref(`/users/${user.uid}/email`)
            .set(email);
          notification.success({ message: t("notifications.106") });
          
          // Close email edit mode
          setChangeEmail(false);
        }
      })
      .catch((e) => {
        notification.error({
          message: `${t("notifications.107")} ` + e.message,
        });
      });
  };

  const updateChangeEmail = () => {
    if (changeEmail) {
      setEmail(user?.email);
      setChangeEmail(false);
    } else {
      setChangeEmail(true);
    }
  };
  // MFA timer
  useEffect(() => {
    if (mfaTimeLeft <= 0) return;
    const t = setTimeout(() => setMfaTimeLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [mfaTimeLeft]);

  const openMFAModal = () => {
    setMfaPhone(user?.phoneNumber || "");
    setMfaSmsSent(false);
    setMfaVerificationCode(undefined);
    setMfaTimeLeft(0);
    setShowMFAModal(true);
    // initialise invisible recaptcha on the hidden div
    setTimeout(() => {
      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = new firebaseApp.auth.RecaptchaVerifier(
          "mfa-recaptcha-container",
          { size: "invisible" }
        );
      }
    }, 300);
  };

  const closeMFAModal = () => {
    setShowMFAModal(false);
    setMfaSmsSent(false);
    setMfaVerificationCode(undefined);
    // reset recaptcha so next open gets a fresh one
    recaptchaVerifierRef.current = null;
  };

  const mfaSendSMS = async () => {
    setMfaLoading(true);
    try {
      const session = await firebase
        .auth()
        .currentUser.multiFactor.getSession();
      const phoneAuthProvider = new firebaseApp.auth.PhoneAuthProvider();
      const vid = await phoneAuthProvider.verifyPhoneNumber(
        { phoneNumber: mfaPhone, session },
        recaptchaVerifierRef.current
      );
      setMfaVerificationId(vid);
      setMfaSmsSent(true);
      setMfaTimeLeft(MFA_TIMER);
    } catch (e) {
      notification.error({ message: e.message });
      recaptchaVerifierRef.current = null;
    } finally {
      setMfaLoading(false);
    }
  };

  const mfaResend = async () => {
    setMfaLoading(true);
    try {
      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = new firebaseApp.auth.RecaptchaVerifier(
          "mfa-recaptcha-container",
          { size: "invisible" }
        );
      }
      const session = await firebase
        .auth()
        .currentUser.multiFactor.getSession();
      const phoneAuthProvider = new firebaseApp.auth.PhoneAuthProvider();
      const vid = await phoneAuthProvider.verifyPhoneNumber(
        { phoneNumber: mfaPhone, session },
        recaptchaVerifierRef.current
      );
      setMfaVerificationId(vid);
      setMfaSmsSent(true);
      setMfaTimeLeft(MFA_TIMER);
    } catch (e) {
      notification.error({ message: e.message });
    } finally {
      setMfaLoading(false);
    }
  };

  const mfaEnable = async () => {
    setMfaLoading(true);
    try {
      const cred = firebaseApp.auth.PhoneAuthProvider.credential(
        mfaVerificationId,
        mfaVerificationCode
      );
      const assertion = firebaseApp.auth.PhoneMultiFactorGenerator.assertion(cred);
      await firebase
        .auth()
        .currentUser.multiFactor.enroll(assertion, "My phone");
      setMFAEnabled(true);
      closeMFAModal();
      notification.success({ message: t("notifications.109") });
    } catch (e) {
      notification.error({ message: e.message });
    } finally {
      setMfaLoading(false);
    }
  };

  const disable2fa = () => {
    const user = firebase.auth().currentUser;
    const uid = user.multiFactor.enrolledFactors[0].uid;
    user.multiFactor
      .unenroll(uid)
      .then(() => {
        setMFAEnabled(
          firebase.auth().currentUser.multiFactor.enrolledFactors.length !== 0
        );
        notification.success({ message: t("notifications.109") });
      })
      .catch((e) => {
        if (e.message === "CREDENTIAL_TOO_OLD_LOGIN_AGAIN") {
          // TODO: getUserCredentials
        }
        notification.error({ message: e.message });
      });
  };

  const resendVerificationEmail = () => {
    // Check cooldown
    if (resendCooldown > 0) {
      return;
    }

    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      notification.error({
        message: "Error",
        description: "User not found. Please refresh the page.",
      });
      return;
    }

    sendAppVerificationEmail({
      uid: currentUser.uid,
      language: preferredVerificationLanguage(i18n.language),
      continueUrl: `${window.location.origin}/home`,
      firstName: user?.firstName,
    }).then((result) => {
      setResendCooldown(60);
      if (result && !result.error && !result.cancel) {
        notification.success({
          message: "Verification Email Sent",
          description: `A verification email has been sent to ${currentUser.email}. Please check your inbox.`,
          duration: 6,
        });
        return;
      }
      logError("Resend Verification Email", result?.error || "send_failed");
      notification.error({
        message: "Failed to Send Verification Email",
        description: "Too many requests. Please wait a few seconds and try again.",
        duration: 6,
      });
    });
  };

  return (
    <>
      {/* Invisible recaptcha container — must stay in DOM */}
      <div id="mfa-recaptcha-container" style={{ display: "none" }} />

      <Representation
        emailVerified={emailVerified}
        email={email}
        setEmail={setEmail}
        saveEmail={saveEmail}
        setChangeEmail={setChangeEmail}
        changeEmail={changeEmail}
        updateChangeEmail={updateChangeEmail}
        setShowPasswordModal={setShowPasswordModal}
        showPasswordModal={showPasswordModal}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        changePassword={changePassword}
        deleteAccount={deleteAccount}
        loading={loading}
        setOldPassword={setOldPassword}
        setNewPassword={setNewPassword}
        setConfirmNewPassword={setConfirmNewPassword}
        disable2fa={disable2fa}
        mfaEnabled={mfaEnabled}
        plan={subscriptionStatus}
        changePwdLoading={changePwdLoading}
        resendVerificationEmail={resendVerificationEmail}
        resendCooldown={resendCooldown}
        // MFA modal props
        showMFAModal={showMFAModal}
        openMFAModal={openMFAModal}
        closeMFAModal={closeMFAModal}
        mfaPhone={mfaPhone}
        mfaSmsSent={mfaSmsSent}
        mfaVerificationCode={mfaVerificationCode}
        setMfaVerificationCode={setMfaVerificationCode}
        mfaTimeLeft={mfaTimeLeft}
        mfaLoading={mfaLoading}
        mfaSendSMS={mfaSendSMS}
        mfaResend={mfaResend}
        mfaEnable={mfaEnable}
      />
    </>
  );
};

export default Security;
